import type { MessageAttachment } from '@wix/ai-scheduling-assistant-common-lib/entities';
import { Edit3, Sparkles } from 'lucide-react';
import { useTranslation } from '../../../../lib/i18n';
import { useGenericBi } from '../../../../lib/use-generic-bi';
import { editInWixelLink } from '../../../../lib/wixel-edit-choice';
import { designReviewFileRequest } from '../../../../lib/design-review';
import { WIXEL_CHAT_ASSET_BI, wixelChatAssetEditorUrl } from '../../../../lib/wixel-chat-asset';
import { Button, IconButton } from '../../ui';
import styles from './WixelAssetActions.module.css';

/** What an assistant row hands its attachments so a Wixel asset can carry the action row. Absent on a user row. */
export interface WixelAssetActionsHost {
  /** Send the design-review request into THIS conversation. Absent on a thread that accepts no sends. */
  onDesignReview?: (text: string) => void;
  /** False while a turn streams: the review button shows disabled rather than send into a refused turn. */
  canSend?: boolean;
}

/**
 * "Edit in Wixel" and "Get a design review", directly under a Wixel asset in an assistant message — the rule
 * and its reasons are in `lib/wixel-chat-asset.ts`. Compact secondary (outlined) buttons, never the accent:
 * the attachment is the thing to look at, these are what you can do with it.
 *
 * `compact` is a tile in the multi-attachment grid, too narrow for labels: the same two actions as icon
 * buttons, labelled for assistive tech and on hover. Same rule on iOS and Android (a grid cell).
 */
export function WixelAssetActions({
  att,
  host,
  compact = false,
}: {
  att: MessageAttachment;
  host: WixelAssetActionsHost;
  compact?: boolean;
}) {
  const { t } = useTranslation();
  const { logPageClickBi } = useGenericBi();
  const editorUrl = wixelChatAssetEditorUrl(att, true);
  if (!editorUrl) return null;

  const link = editInWixelLink(editorUrl);
  const editLabel = t('fileActions.editInWixel');
  const reviewLabel = t('designReview.action');
  const logEdit = () => logPageClickBi({ page_name: 'chat', button_name: WIXEL_CHAT_ASSET_BI.edit, answer: att.fileName });
  const edit = () => {
    logEdit();
    if (link.onSelect) link.onSelect();
    else window.open(link.href, '_blank', 'noopener,noreferrer');
  };
  const { onDesignReview, canSend = true } = host;
  const review = onDesignReview
    ? () => {
        logPageClickBi({ page_name: 'chat', button_name: WIXEL_CHAT_ASSET_BI.designReview, answer: att.fileName });
        onDesignReview(designReviewFileRequest(t, att.fileName ?? ''));
      }
    : undefined;

  if (compact) {
    return (
      <div className={styles.row} data-testid="wixel-asset-actions">
        <IconButton size="tiny" variant="outlined" onClick={edit} aria-label={editLabel} title={editLabel}>
          <Edit3 size={14} strokeWidth={1.5} absoluteStrokeWidth aria-hidden />
        </IconButton>
        {review && (
          <IconButton size="tiny" variant="outlined" onClick={review} disabled={!canSend} aria-label={reviewLabel} title={reviewLabel}>
            <Sparkles size={14} strokeWidth={1.5} absoluteStrokeWidth aria-hidden />
          </IconButton>
        )}
      </div>
    );
  }
  return (
    <div className={styles.row} data-testid="wixel-asset-actions">
      {link.href ? (
        // Desktop: a real anchor to the editor in a new tab, exactly as the viewer's menu item.
        <Button
          variant="outlined"
          size="tiny"
          href={link.href}
          external
          target="_blank"
          rel="noopener noreferrer"
          onClick={logEdit}
          startIcon={<Edit3 size={14} strokeWidth={1.5} absoluteStrokeWidth aria-hidden />}
        >
          {editLabel}
        </Button>
      ) : (
        <Button variant="outlined" size="tiny" onClick={edit} startIcon={<Edit3 size={14} strokeWidth={1.5} absoluteStrokeWidth aria-hidden />}>
          {editLabel}
        </Button>
      )}
      {review && (
        <Button
          variant="outlined"
          size="tiny"
          onClick={review}
          disabled={!canSend}
          startIcon={<Sparkles size={14} strokeWidth={1.5} absoluteStrokeWidth aria-hidden />}
        >
          {reviewLabel}
        </Button>
      )}
    </div>
  );
}
